import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useGamePlayStore } from '@/stores/gamePlayStore';

export interface ModalMessage {
  title: string | false;
  body: string | false;
  actionButton?: {
    action: (() => void) | null;
    text: string | null;
    movedUpALevel?: boolean;
  };
  closeButton?: boolean;
  backgroundImage?: string;
}

export const useModalStore = defineStore('modal', () => {
  const isVisible = ref(false);
  const message = ref<ModalMessage>({
    title: false,
    body: false,
    actionButton: { action: null, text: null },
  });

  const levelUpMessage = ref<ModalMessage>({
    title: false,
    body: false,
    actionButton: { action: null, text: null },
  });

  const isLevelUpModalVisible = ref(false);

  function showModal(modalMessage: ModalMessage) {
    message.value = modalMessage;
    isVisible.value = true;
  }

  function hideModal() {
    message.value = {
      title: false,
      body: false,
      actionButton: { action: null, text: null },
    };
    isVisible.value = false;
  }

  function setModalMessage({
    title,
    body,
    actionButton,
  }: {
    title: string | false;
    body: string | false;
    actionButton?: ModalMessage['actionButton'];
  }) {
    message.value = { title, body, actionButton };
    isVisible.value = true;
  }

  function resetModalMessage() {
    message.value = {
      title: false,
      body: false,
      actionButton: { action: null, text: null },
    };
    isVisible.value = false;
  }

  function setBackgroundImage(image: string) {
    if (message.value) {
      message.value.backgroundImage = image;
    }
  }

  function showLevelUpModal(modalMessage: ModalMessage) {
    const gamePlayStore = useGamePlayStore();

    // First hide any existing modal to prevent overlapping modals
    isVisible.value = false;
    resetModalMessage();

    modalMessage['title'] = 'Level Up!';
    modalMessage['body'] =
      `Great job! You've reached level ${gamePlayStore.level}!`;
    levelUpMessage.value = modalMessage;
    isLevelUpModalVisible.value = true; // Set this to true instead of isVisible
  }

  function hideLevelUpModal() {
    levelUpMessage.value = {
      title: false,
      body: false,
      actionButton: { action: null, text: null },
    };
    isLevelUpModalVisible.value = false;
  }

  function showEndOfGameModal(): void {
    const gamePlayStore = useGamePlayStore();
    const modalMessage: ModalMessage = {
      title: 'Congratulations!',
      body: 'You have completed the game!',
      actionButton: {
        action: () => {
          // Navigate back to home when the action button is clicked
          gamePlayStore.exitGame();
        },
        text: 'Continue',
      },
      closeButton: true,
    };

    showModal(modalMessage);
  }

  return {
    // State
    isVisible,
    message,
    setModalMessage,
    resetModalMessage,
    showModal,
    hideModal,
    setBackgroundImage,
    isLevelUpModalVisible,
    levelUpMessage,
    showLevelUpModal,
    hideLevelUpModal,
    showEndOfGameModal,
  };
});
